// ---------------------------------------------------------------------------
// LA RÉCLAMATION QUOTIDIENNE — revenir chaque jour, et que ça se voie
//
// Toji, 14/09 : « reprends le design de la daily […] tu remets le même niveau
// et la daily, techniquement. » Les chiffres sont ceux du bot Roma, relevés
// dans son code : 50 de base, 5 de bonus par jour de série, 100 de bonus au
// maximum, série retenue plafonnée à 7 jours.
//
// Ce fichier ne fait que du calendrier et de l'arithmétique. Il ne lit rien,
// n'écrit rien, et ne regarde JAMAIS l'horloge lui-même : la date entre par les
// paramètres. C'est ce qui permet au banc de faire passer un mois — et deux
// changements d'heure — en quelques lignes, et c'est aussi ce qui rend le
// script Lua du magasin possible (un script Redis n'a pas d'horloge fiable :
// le jour lui est PASSÉ en argument, calculé ici).
//
// ⚠️ TROIS PIÈGES, PAYÉS EN PRODUCTION PAR ROMA, ET QU'ON NE REPAIE PAS :
//
//  1. LA SÉRIE QUI GONFLE. Un `serie + 1` naïf à chaque réclamation, et un
//     double-clic ou une remise à zéro d'admin la faisait exploser. Ici la série
//     ne s'incrémente pas : elle se RECALCULE depuis le dernier jour réclamé
//     (`serieApres`). Déterministe, et auto-réparant — une donnée absurde en
//     base redevient cohérente à la première réclamation suivante.
//
//  2. LE FUSEAU. Tous les « une fois par jour » se comptent sur minuit
//     EUROPE/PARIS, jamais UTC. Chez Roma, un tick à cheval sur minuit décalait
//     le seuil : l'été, minuit UTC tombe à 2 h du matin à Paris, et la
//     réclamation « de demain » était déjà possible le soir même.
//
//  3. LE DOUBLE-CLIC. Corrigé chez Roma par un verrou Redis plus une
//     transaction Serializable. Ici, c'est plus simple et plus solide : lecture,
//     calcul et écriture tiennent dans UN script Lua (`magasin-points.js`),
//     que Redis exécute d'un seul tenant. Rien ne s'intercale.
// ---------------------------------------------------------------------------

/**
 * Le barème par défaut, celui de Roma.
 *
 * ⚠️ AVEC CES DÉFAUTS, `maxBonus` NE SERT JAMAIS : la série est retenue à 7
 * jours, donc le bonus plafonne à 7 × 5 = 35, bien avant 100. Les deux bornes
 * existent quand même parce qu'elles gardent des choses différentes — `maxJours`
 * borne la FIDÉLITÉ qu'on récompense, `maxBonus` borne la DÉPENSE — et qu'un
 * exploitant qui monte `parJour` à 30 depuis l'écran d'administration retrouve
 * aussitôt la seconde. Ce sont les chiffres de Roma, on ne les « corrige » pas.
 */
export const DAILY_DEFAUTS = Object.freeze({
  base: 50,
  parJour: 5,
  maxBonus: 100,
  maxJours: 7,
});

/**
 * Le fuseau de référence. Un seul, celui du serveur — pas celui de la personne.
 *
 * Une daily « par personne et par fuseau » ouvrirait un trou : il suffirait de
 * déclarer un fuseau plus à l'est pour réclamer deux fois dans la même soirée.
 */
export const FUSEAU = 'Europe/Paris';

/**
 * ⚠️ CONSTRUIT UNE FOIS, PAS À CHAQUE APPEL. Un `Intl.DateTimeFormat` coûte
 * environ cent fois une mise en forme, et `jourDe` est appelé à chaque carte.
 *
 * S'il n'y a pas d'ICU complet dans le Node qui tourne, ce constructeur LÈVE au
 * chargement du module — et c'est voulu. Un repli silencieux sur UTC ferait
 * exactement le piège n° 2, mais sans que personne ne le voie.
 */
const FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSEAU, year: 'numeric', month: '2-digit', day: '2-digit',
});

const JOUR = /^(\d{4})-(\d{2})-(\d{2})$/;
const deuxChiffres = (n) => String(n).padStart(2, '0');

/**
 * Le jour civil d'un instant, à Paris : `'2026-09-13'`.
 *
 * ⚠️ C'EST UNE DATE CIVILE, PAS UN INSTANT, et c'est la distinction qui rend
 * tout le reste juste. Deux instants séparés de 25 heures peuvent appartenir au
 * même jour civil (le dimanche de fin octobre dure 25 heures) ; deux instants
 * séparés de 23 heures peuvent être deux jours différents. Tout ce qui suit
 * raisonne sur ces chaînes, jamais sur des millisecondes.
 *
 * `formatToParts` plutôt que `format` : la locale `en-CA` rend bien
 * `AAAA-MM-JJ` aujourd'hui, mais c'est une donnée d'ICU, pas un contrat. En
 * lisant les morceaux, on ne dépend que des noms de champs.
 */
export function jourDe(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const t = d.getTime();
  // Une date invalide ne doit pas devenir un jour bidon : elle deviendrait une
  // série rompue pour tout le serveur, en silence.
  if (!Number.isFinite(t)) throw new TypeError(`jourDe : date invalide (${date})`);
  const p = {};
  for (const { type, value } of FORMAT.formatToParts(d)) p[type] = value;
  return `${p.year}-${p.month}-${p.day}`;
}

/** Un jour bien formé, ou `''`. Rien d'autre n'entre dans les comparaisons. */
export function normaliserJour(brut) {
  const s = String(brut ?? '').trim();
  return JOUR.test(s) ? s : '';
}

/**
 * La veille d'un jour civil.
 *
 * ⚠️ JAMAIS « l'instant moins 86 400 000 ». Deux fois par an, un jour de Paris
 * ne dure pas 24 heures : le dernier dimanche de mars en dure 23, celui
 * d'octobre 25. Retirer une journée fixe à un instant tombe alors sur
 * l'avant-veille ou sur le jour même — et la série de tout le monde se rompt
 * (ou se double) cette nuit-là. Ici on retire un jour de CALENDRIER sur une
 * date sans fuseau : le 30 mars moins un jour fait le 29 mars, changement
 * d'heure ou pas.
 *
 * `setUTCFullYear` plutôt que `Date.UTC(y, …)` : ce dernier traite les années
 * 0 à 99 comme 1900 à 1999. Impossible avec un jour venu de `jourDe`, mais on
 * ne laisse pas une fonction publique dépendre de qui l'appelle.
 */
export function veilleDe(jour) {
  const m = JOUR.exec(normaliserJour(jour));
  if (!m) return '';
  const d = new Date(0);
  d.setUTCFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setUTCDate(d.getUTCDate() - 1);
  return `${d.getUTCFullYear()}-${deuxChiffres(d.getUTCMonth() + 1)}-${deuxChiffres(d.getUTCDate())}`;
}

/**
 * Ce que devient la série si l'on réclame `jour`, sachant qu'on a réclamé pour
 * la dernière fois `dernierJour` avec une série de `serie`.
 *
 * Rend `{ serie, rompue, dejaFait }` :
 *   · `dejaFait` — c'est déjà fait aujourd'hui, il n'y a rien à créditer ;
 *   · `rompue`   — un jour a été sauté, la série repart de 1 ;
 *   · `serie`    — la série APRÈS cette réclamation.
 *
 * ⚠️ ELLE SE RECALCULE, ELLE NE S'INCRÉMENTE PAS (piège n° 1). Trois cas, et
 * trois seulement : même jour, veille, ou autre chose. « Autre chose » couvre
 * aussi le jour SUIVANT — une horloge qui a reculé, une date posée à la main
 * par un admin : la série repart de 1 au lieu de créditer un futur. Aucun état
 * intermédiaire ne peut la faire enfler.
 */
export function serieApres(dernierJour, jour, serie = 0) {
  const aujourdhui = normaliserJour(jour);
  // Un jour illisible ici est une faute d'appel, pas une donnée d'utilisateur :
  // l'avaler créditerait n'importe quoi. `dernierJour`, lui, vient de la base et
  // a le droit d'être vide ou abîmé.
  if (!aujourdhui) throw new TypeError(`serieApres : jour invalide (${jour})`);
  const dernier = normaliserJour(dernierJour);
  const brute = Math.floor(Number(serie));
  // Une série stockée à 0 alors qu'un jour est enregistré est incohérente : on
  // la relève à 1, puisqu'il y a bien eu au moins une réclamation.
  const acquise = Number.isFinite(brute) && brute > 1 ? brute : 1;

  if (dernier && dernier === aujourdhui) return { serie: acquise, rompue: false, dejaFait: true };
  if (dernier && dernier === veilleDe(aujourdhui)) return { serie: acquise + 1, rompue: false, dejaFait: false };
  // Première réclamation : ce n'est pas une rupture, c'est un début.
  return { serie: 1, rompue: Boolean(dernier), dejaFait: false };
}

/** Un réglage de barème : entier, jamais négatif, sinon son défaut. */
function positif(brut, defaut) {
  const n = Math.floor(Number(brut));
  return Number.isFinite(n) && n >= 0 ? n : defaut;
}

/**
 * Ce que rapporte une réclamation, pour une série donnée.
 *
 * Rend `{ base, bonus, total, serie }`, où `serie` est la série RETENUE pour le
 * calcul — plafonnée à `maxJours`. La série réelle, elle, continue de compter
 * les jours d'affilée sans limite : quelqu'un qui vient depuis 40 jours mérite
 * de le lire sur sa carte, même si le bonus ne monte plus après 7.
 *
 * La formule est celle de Roma : `bonus = min(serie × parJour, maxBonus)`, avec
 * une série qui vaut 1 le premier jour. Une réclamation isolée rapporte donc
 * 55, pas 50 — « cinq par jour de série », et le premier jour EST un jour de
 * série.
 *
 * ⚠️ AUCUN MULTIPLICATEUR DE RÔLE ICI, contrairement au vocal et aux messages.
 * La daily récompense le fait de REVENIR, pas le rang : un rôle à 2 × rendrait
 * la fidélité deux fois plus payante pour ceux qui ont déjà le plus, et le
 * geste quotidien n'a rien à voir avec le temps donné.
 */
export function montantDuDaily(serie, reglages = DAILY_DEFAUTS) {
  const r = reglages && typeof reglages === 'object' ? reglages : DAILY_DEFAUTS;
  const base = positif(r.base, DAILY_DEFAUTS.base);
  const parJour = positif(r.parJour, DAILY_DEFAUTS.parJour);
  const maxBonus = positif(r.maxBonus, DAILY_DEFAUTS.maxBonus);
  const maxJours = Math.max(1, positif(r.maxJours, DAILY_DEFAUTS.maxJours));

  const brute = Math.floor(Number(serie));
  const retenue = Math.min(Number.isFinite(brute) && brute > 1 ? brute : 1, maxJours);
  const bonus = Math.min(retenue * parJour, maxBonus);
  return { base, bonus, total: base + bonus, serie: retenue };
}
